// Migrated from Nemo library https://github.com/pearson-englishlanguagelearning/nemo-component-library/tree/main/packages/core-library/src/components/Feedback/alert/alert-standalone/v1

import { useCallback, useEffect, useRef, useState, FC, ReactNode, CSSProperties } from 'react';
import { NIcon } from '../NIcon/NIcon';
import NTypography from '../NTypography/NTypography';
import './NAlertStandalone.css';

export type AlertType = 'success' | 'info' | 'error' | 'warning';
export type ReadDirection = 'ltr' | 'rtl' | 'auto';

export interface AlertStandaloneProps {
  /** The supporting title (if any) to show */
  heading?: string;
  /** The type of alert to show */
  type: AlertType;
  /** Controls whether the alert is visible or hidden */
  showAlert?: boolean;
  /** Controls whether the alert should have a close icon */
  dismissible?: boolean;
  /** Text to show for action button */
  actionText?: string;
  /** An event which fires when the alert is closed */
  onAlertClosed?: () => void;
  /** An event which fires when the action is clicked */
  onActionClick?: () => void;
  /** Content to display in the alert */
  children?: ReactNode;
  /** Optional className for custom styling */
  className?: string;
  /** Optional inline styles */
  style?: CSSProperties;
}

export interface IconNameMapping {
  success: 'check-stroke';
  info: 'info-stroke';
  error: 'cross-stroke';
  warning: 'warning-stroke';
}

/**
 * Standalone alerts provide timely and contextual information to users,
 * guiding them through specific situations like successes, warnings, errors, or informational updates.
 */
export const NAlertStandalone: FC<AlertStandaloneProps> = ({
  heading,
  type,
  showAlert = false,
  dismissible = false,
  actionText,
  onAlertClosed,
  onActionClick,
  children,
  className,
  style
}) => {
  const [computedDir, setComputedDir] = useState<'ltr' | 'rtl'>('ltr');
  const alertRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);

  // Icon mapping based on alert type
  const iconMapping: IconNameMapping = {
    success: 'check-stroke',
    info: 'info-stroke',
    error: 'cross-stroke',
    warning: 'warning-stroke'
  };

  /**
   * Determines the read direction based on the computed CSS direction
   */
  const checkReadDirection = useCallback(() => {
    if (alertRef.current) {
      const computedStyle = window.getComputedStyle(alertRef.current);
      setComputedDir(computedStyle.direction as 'ltr' | 'rtl');
    }
  }, []);

  /**
   * Handles closing the alert
   */
  const closeAlert = useCallback(() => {
    onAlertClosed?.();
  }, [onAlertClosed]);

  /**
   * Handles action button click
   */
  const handleActionClick = useCallback(() => {
    onActionClick?.();
  }, [onActionClick]);

  /**
   * Check read direction on mount
   */
  useEffect(() => {
    checkReadDirection();
  }, [checkReadDirection]);

  const alertClasses = [
    'n-alert-standalone',
    `n-alert-standalone-${type}`,
    `n-alert-standalone-${computedDir}`,
    showAlert ? 'n-alert-standalone-visible' : '',
    className || ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={alertRef}
      className={alertClasses}
      style={style}
      role='dialog'
      aria-labelledby='alert-heading'
      aria-describedby='alert-description'
      aria-live='polite'
      aria-atomic={true}
    >
      <div className='n-alert-standalone-content'>
        <span className='n-alert-standalone-icon'>
          <NIcon name={iconMapping[type]} size='md' />
        </span>
        <span>
          {heading && (
            <span id='alert-heading'>
              <NTypography variant='b2-strong'>{heading}</NTypography>
            </span>
          )}
          <span id='alert-description'>
            <NTypography variant='b2' noMargin>
              {children}
            </NTypography>
          </span>
          {actionText && (
            <button
              className='n-alert-standalone-action'
              onClick={handleActionClick}
              ref={actionButtonRef}
            >
              <NTypography variant='b2-strong' noMargin>
                {actionText}
              </NTypography>
            </button>
          )}
        </span>
      </div>
      <span className='n-alert-standalone-content'>
        {dismissible && (
          <button
            className='n-alert-standalone-close-icon'
            onClick={closeAlert}
            aria-label='Close'
            ref={closeButtonRef}
          >
            <NIcon name='cross-circle-stroke' size='md' />
          </button>
        )}
      </span>
    </div>
  );
};
